import { Component, inject, OnInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { BehaviorSubject, combineLatest, Observable, of } from 'rxjs';
import { filter, mapTo, shareReplay, switchMap } from 'rxjs/operators';
import { Restriction } from '../../core/api/entities/api-query.entity';
import { BaseContainer } from '../../core/common/containers/base.abstract.container';
import { DottedKey } from '../../core/field-definitions/entities/field-definition.entity';
import { FacetProfileTag, ListProfileTag } from '../../core/profiles/data-services/profile.data-service';
import { ApiFragment } from '../../core/records/entities/api-fragment.class';
import { ActionGroup } from '../../elements/components/action-group/entities/action-group.entity';
import { ActionItem } from '../../elements/components/action-item/entities/action-item.entity';
import { NotificationType } from '../../elements/components/notification/entities/notification-types.entity';
import { isNotNullOrUndefined } from '../../helpers/typed-reactive-filters.helper';
import { SortInfo } from '../../overviews/generic-overview/components/display-settings-bar/entities/sort-info.entity';
import { PaginatedResults } from '../../overviews/generic-overview/components/pagination/entities/paginated-results.entity';
import { ColumnDef } from '../../overviews/list-view/entities/column-definition.entity';
import { SearchBarMode } from '../../overviews/list-view/entities/filter-bar-mode.entity';
import { SearchPageFeatures } from '../../overviews/search-page/entities/search-page-config.entity';
import { Panel } from '../../overviews/search-page/entities/search-page-state.entity';
import {
    ActionsServiceInterface,
    BatchActionsServiceInterface,
    BulkActionsServiceInterface
} from '../../overviews/search-page/services/actions.service.interface';
import { SearchPageStateService } from '../../overviews/search-page/state-machines/search-page-state.service';
import { getShareContext } from '../../session-storage/helpers/fetch-from-session-storage/session-storage.helper';
import { QueryIteratorStack } from '../entities/query-iterator-stack.entity';
import { RecordSearchPageConfig } from '../entities/record-search-page-config.entity';

@Component({
    standalone: true,
    selector: 'sh-record-search-page-impl',
    template: ''
})
export abstract class RecordSearchPageImplContainerComponent extends BaseContainer implements OnInit {
    public recordSearchPageConfig: RecordSearchPageConfig;

    // Notifications
    public readonly notificationType: typeof NotificationType = NotificationType;

    // Identifiers
    protected abstract readonly PAGE_ID: string;
    protected abstract readonly PAGE_TITLE: string;

    // Context
    protected CONTEXT_RECORD$: Observable<ApiFragment | undefined> = of(undefined);
    protected BACK_TO_CONTEXT_LABEL: string;

    public ACTIVE_RECORD$: BehaviorSubject<ApiFragment | undefined> = new BehaviorSubject<ApiFragment | undefined>(
        undefined
    );

    public QUERY_ITERATOR_STACK$: BehaviorSubject<QueryIteratorStack | undefined> = new BehaviorSubject<
        QueryIteratorStack | undefined
    >(undefined);

    // Page Defaults
    protected abstract readonly DEFAULT_SORT_MODE: SortInfo;
    protected abstract readonly DEFAULT_SORT_OPTIONS: SortInfo[];

    // SearchBar
    protected abstract readonly SEARCH_BAR_MODE: SearchBarMode;
    protected abstract readonly SEARCH_BAR_ACTIONS_FCT: () => Observable<ActionItem[]>;

    // Search
    protected abstract readonly RESTRICTIONS$: Observable<Restriction>;
    protected abstract readonly FIELD_SELECT: DottedKey[];

    // Profiles
    protected abstract readonly FACET_PROFILE_TAG: FacetProfileTag;
    protected abstract readonly LIST_PROFILE_TAG: ListProfileTag;
    protected readonly COLUMNS: ColumnDef<any, ApiFragment>[];

    // Actions
    protected abstract readonly RECORD_ACTION_TYPES: readonly string[];
    protected abstract readonly BULK_RECORD_ACTION_TYPES: readonly string[];
    protected abstract readonly BATCH_RECORD_ACTION_TYPES: readonly string[];

    // Features
    protected readonly FEATURES: Record<SearchPageFeatures | Panel, boolean> = {
        [SearchPageFeatures.Selection]: true,
        [SearchPageFeatures.BatchSelection]: true,
        [SearchPageFeatures.GridView]: true,
        [SearchPageFeatures.MapView]: true,
        [Panel.FILTERS]: true,
        [Panel.HIERARCHY]: true,
        [Panel.DETAIL]: true,
        [SearchPageFeatures.Sort]: true,
        [SearchPageFeatures.AutoRefresh]: false,
        [SearchPageFeatures.PreviewOnThumbnailClick]: true,
        [SearchPageFeatures.RowClickAction]: true
    };

    // Translations
    protected ITEM_SELECTED_LABEL_KEY: string = 'general.xx-selected.generic';
    protected NO_ITEM_SELECTED_LABEL_KEY: string = 'general.none-selected.generic';
    protected RESULTS_LABEL_KEY: string = 'general.xx-results.generic';
    protected NO_RESULTS_LABEL_KEY: string = 'general.no-results.generic';
    protected BACK_TO_LABEL_KEY: string;

    // Sandboxes
    protected abstract readonly actionsSandbox: ActionsServiceInterface<ApiFragment>;
    protected abstract readonly bulkActionsSandbox: BulkActionsServiceInterface<ApiFragment>;
    protected abstract readonly batchActionsSandbox: BatchActionsServiceInterface<ApiFragment>;

    protected readonly route: ActivatedRoute = inject(ActivatedRoute);
    private readonly searchPageStateService: SearchPageStateService = inject(SearchPageStateService);

    ngOnInit(): void {
        this.initBackToLabel();
        this.initSearchPageConfig();
    }

    private initSearchPageConfig(): void {
        this.recordSearchPageConfig = {
            id: this.PAGE_ID,
            title: this.PAGE_TITLE,
            features: this.FEATURES,
            sort: {
                default: this.DEFAULT_SORT_MODE,
                fixedOptions: this.DEFAULT_SORT_OPTIONS
            },
            search: {
                queryRestriction$: this.RESTRICTIONS$,
                fieldSelect: this.FIELD_SELECT,
                searchBarMode: this.SEARCH_BAR_MODE,
                refreshTriggers: this.getRefreshTriggers(),
                getSearchBarActionsFct: this.SEARCH_BAR_ACTIONS_FCT
            },
            actions: {
                getGlobalActionsFct: this.getGlobalActions.bind(this),
                getActionsFct: this.getActions.bind(this),
                getBulkActionsFct: this.getBulkActions.bind(this),
                getBatchActionsFct: this.getBatchActions.bind(this),
                onRowClickFct: this.onRowClickFct.bind(this)
            },
            columns: this.COLUMNS,
            profileTags: {
                list: this.LIST_PROFILE_TAG,
                facets: this.FACET_PROFILE_TAG
            },
            translations: {
                itemsSelectedLabelKey: this.ITEM_SELECTED_LABEL_KEY,
                noItemsSelectedLabelKey: this.NO_ITEM_SELECTED_LABEL_KEY,
                resultsLabelKey: this.RESULTS_LABEL_KEY,
                noResultsLabelKey: this.NO_RESULTS_LABEL_KEY,
                backToLabelKey: this.BACK_TO_LABEL_KEY
            }
        };
    }

    // #region Actions
    private getRefreshTriggers(): Observable<boolean>[] {
        const triggers = [];

        const actionRefresh$ = this.actionsSandbox.performedActions$.pipe(
            filter(action => action.warrantsRefresh),
            mapTo(true)
        );

        triggers.push(actionRefresh$);

        if (this.bulkActionsSandbox) {
            const bulkActionRefresh$ = this.bulkActionsSandbox.performedActions$.pipe(
                filter(action => action.warrantsRefresh),
                mapTo(true)
            );
            triggers.push(bulkActionRefresh$);
        }

        return triggers;
    }

    protected getGlobalActions(): Observable<ActionItem[]> {
        return of([]);
    }

    protected getActions(records: ApiFragment[], queryIteratorStack: QueryIteratorStack): Observable<ActionGroup[]> {
        if (records.length === 0) {
            return of([]);
        }

        const shareContext$: Observable<string | undefined> = getShareContext(this.route);

        return combineLatest([this.CONTEXT_RECORD$, shareContext$]).pipe(
            switchMap(([contextRecord, shareContext]) =>
                combineLatest(
                    records.map(record =>
                        this.actionsSandbox.getRegularActionsWithIconActions(this.RECORD_ACTION_TYPES, record, {
                            queryIteratorStack,
                            parent: contextRecord,
                            shareContext
                        })
                    )
                )
            )
        );
    }

    protected getBulkActions(records: ApiFragment[], queryIteratorStack: QueryIteratorStack): Observable<ActionItem[]> {
        if (!this.bulkActionsSandbox) {
            return of([]);
        }

        const shareContext$: Observable<string | undefined> = getShareContext(this.route);

        return combineLatest([this.CONTEXT_RECORD$, shareContext$]).pipe(
            switchMap(([contextRecord, shareContext]) =>
                this.bulkActionsSandbox.getBulkActionsReduced(this.BULK_RECORD_ACTION_TYPES, records, {
                    queryIteratorStack,
                    contextFragment: contextRecord,
                    shareContext
                })
            )
        );
    }

    protected getBatchActions(
        paginatedResults: PaginatedResults<ApiFragment>,
        queryIteratorStack: QueryIteratorStack
    ): Observable<ActionItem[]> {
        if (!this.batchActionsSandbox) {
            return of([]);
        }

        return combineLatest([this.CONTEXT_RECORD$, this.RESTRICTIONS$]).pipe(
            switchMap(([contextRecord, restrictions]) =>
                this.batchActionsSandbox.getBatchActionsReduced(this.BATCH_RECORD_ACTION_TYPES, {
                    queryIteratorStack,
                    contextFragment: contextRecord,
                    paginatedResults,
                    restrictions
                })
            ),
            shareReplay()
        );
    }
    // #endregion

    // #region Back to

    protected initBackToLabel(): void {
        this.CONTEXT_RECORD$.pipe(isNotNullOrUndefined()).subscribe(contextRecord =>
            this.getBackToLabel(contextRecord)
        );
    }

    /**
     * Tries to find translation for back button for specific record type
     * If not found, returns default translation
     *
     * @param contextRecord
     * @returns Label for the back to button
     */
    private getBackToLabel(contextRecord: ApiFragment): string {
        const labelKey = `components.pagination.back-to.${contextRecord.getRecordType().toLowerCase()}`;
        const label: string = this.translateService.translate(labelKey);

        return label !== labelKey ? label : this.translateService.translate('components.pagination.back-to.parent');
    }
    // #endregion

    // #region Events
    private onRowClickFct(record: ApiFragment, queryIteratorStack: QueryIteratorStack): void {
        this.QUERY_ITERATOR_STACK$.next(queryIteratorStack);
        this.ACTIVE_RECORD$.next(record);
    }

    public onCloseDetail(): void {
        this.ACTIVE_RECORD$.next(undefined);
        this.searchPageStateService.savePanelState(this.PAGE_ID, Panel.DETAIL, false);
    }
    // #endregion
}
